<template>
  <div>
    <BulkDiscountButton @click="openModal">
      <span v-if="quantityStore.quantity">{{
        t('product.quantity.pcs', quantityStore.quantity)
      }}</span>
      <span v-else>{{ t('product.quantity.text_select_quantity') }}</span>

      <template
        v-if="selectedBulkDiscountPct || !quantityStore.quantity"
        #highlight
      >
        <template v-if="highestDiscountItemPct && !quantityStore.quantity">{{
          t('product.quantity.text_save_up_to', {
            value: highestDiscountItemPct
          })
        }}</template>
        <template v-else-if="selectedBulkDiscountPct">{{
          t('product.quantity.text_save', { value: selectedBulkDiscountPct })
        }}</template>
      </template>
    </BulkDiscountButton>

    <BulkDiscountModal v-model:active="quantityStore.showQuantityModal">
      <template #header>
        <ModalHeader>{{ modalTitle }}</ModalHeader>
        <ModalSubheader>
          <Tag v-if="tag" color="gray">{{ tag }}</Tag>
          <span v-if="shortDescription"> {{ shortDescription }}</span>
        </ModalSubheader>
      </template>
      <template #header-mobile>
        <ModalHeader>{{ modalTitle }}</ModalHeader>
      </template>
      <template v-if="image" #image>
        <img :src="image" class="h-auto w-full" :alt="modalTitle" />
      </template>

      <div class="mb-1 grid grid-cols-3 gap-2 md:grid-cols-4">
        <div class="pl-4 text-sm font-bold">
          {{ t('product.quantity.entry_qty') }}
        </div>
        <div class="pl-2 text-sm font-bold">
          {{ t('product.quantity.text_discount') }}
        </div>
        <div class="text-right text-sm font-bold">
          {{ t('product.quantity.text_discount_each') }}
        </div>
        <div
          class="hidden pr-4 text-right text-sm font-bold text-pink md:block"
        >
          {{ t('product.quantity.text_label_yousave') }}
        </div>
      </div>

      <template v-for="(bulk, index) in bulkDiscounts">
        <RadioInputBar
          v-if="bulk.quantity"
          :key="index"
          v-model:model-value="qty"
          class="grid grid-cols-3 odd:bg-white even:bg-gray-100/50 md:grid-cols-4"
          :value="bulk.quantity"
        >
          <template #label>
            <span>{{ t('product.quantity.pcs', bulk.quantity) }}</span>
          </template>
          <span>
            <Tag
              v-if="bulk.discount_percentage"
              color="yellow"
              class="font-semibold"
              >{{
                t('product.quantity.text_save', {
                  value: bulk.discount_percentage
                })
              }}</Tag
            >
          </span>
          <span v-if="bulk.price_unit" class="text-right">{{
            formattedPrice(bulk.price_unit)
          }}</span>
          <span
            v-if="getDiscountFormatted(bulk)"
            class="hidden text-right text-pink md:block"
            >{{ getDiscountFormatted(bulk) }}</span
          ><span v-else class="hidden text-right md:block">-</span>
        </RadioInputBar>
      </template>

      <template #controls>
        <NumberSpinner
          v-model="qty"
          :min="0"
          :max="9999"
          :input-label="t('product.quantity.aria_label_amount')"
          :decrease-label="t('product.quantity.aria_label_reduce')"
          :increase-label="t('product.quantity.aria_label_increase')"
        />

        <BulkDiscountPrice
          v-if="closestQuantityItem && qty > 0"
          :prefix="t('product.quantity.text_total_price')"
          :sales-price="salesPrice"
          :regular-price="regularPrice"
        ></BulkDiscountPrice>
      </template>

      <template #footer>
        <ButtonAddToCart
          v-if="inStock && isOptionSelected"
          ref="ButtonAddToCartBulkQty"
          class="w-full min-w-80 p-4 md:w-auto"
          :disabled="qty <= 0 || !inStock || !isOptionSelected"
          @click="addToCart"
        />
        <ButtonText
          v-else
          theme="primary"
          size="lg"
          class="mt-4 w-full rounded p-4 md:w-auto"
          :disabled="qty <= 0"
          @click="submitQuantityAndClose"
        >
          {{ t('product.quantity.text_select_quantity') }}
        </ButtonText>
      </template>
    </BulkDiscountModal>
  </div>
</template>

<script setup lang="ts">
import {
  BulkDiscountButton,
  BulkDiscountPrice,
  ModalHeader,
  ModalSubheader,
  NumberSpinner,
  RadioInputBar,
  Tag
} from '@hobbii/hobbii-ui';
import { computed, onBeforeMount, ref, useTemplateRef } from 'vue';

import BulkDiscountModal from '@/components/QuantitySelector/BulkDiscountModal.vue';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import { useProductMeta } from '@/shared/composables/useProductMeta.js';
import { useTranslations } from '@/shared/composables/useTranslations.ts';
import { useCartStore } from '@/shared/pinia/modules/cart.ts';
import { useConfigStore } from '@/shared/pinia/modules/config';
import { useProductStore } from '@/shared/pinia/modules/product';
import { useProductOptionsStore } from '@/shared/pinia/modules/product-options.ts';
import { useProductQuantitiesStore } from '@/shared/pinia/modules/product-quantities';
import { getErrorMessage } from '@/shared/utils/error';
import { getProductDataFromHTML } from '@/shared/utils/product.js';

import ButtonAddToCart from '../CartActions/ButtonAddToCart.vue';

const { t } = useTranslations();

// We have no way to ensure that this is what the store returns
type MaybeStoreBulkDiscount = {
  quantity?: number;
  discount_percentage?: number;
  price_before?: number;
  price_unit?: number;
};

type MaybeStoreSelectedVariant = {
  title?: string;
  inStock: boolean;
};

const productStore = useProductStore();
const cartStore = useCartStore();
const optionsStore = useProductOptionsStore();
const quantityStore = useProductQuantitiesStore();
const { formattedPrice } = useConfigStore();
const ButtonAddToCartBulkQtyRef = useTemplateRef('ButtonAddToCartBulkQty');

const qty = ref(0);

const addToCart = async () => {
  try {
    quantityStore.updateQuantity(qty.value);
    await cartStore.addMainProductToCart();
    quantityStore.showQuantityModal = false;
  } catch (err) {
    const errorMessage = getErrorMessage(err);

    if (errorMessage) {
      ButtonAddToCartBulkQtyRef?.value?.displayError(errorMessage);
    }
  }
};

const {
  productName,
  entityType,
  metaSubtitle,
  metaYarnComposition,
  metaYarnWeight
}: {
  productName?: string;
  entityType?: string;
  metaSubtitle?: string;
  metaYarnComposition?: string;
  metaYarnWeight?: string;
} = getProductDataFromHTML();

onBeforeMount(() => {
  productStore.setInitialPriceFromDOM();
});

const { getHighlight, getShortDescription } = useProductMeta(
  {
    entity_type: entityType,
    yarn_weight_category: metaYarnWeight,
    yarn_composition: metaYarnComposition,
    subtitle: metaSubtitle
  },
  true
);

const tag = getHighlight();
const shortDescription = getShortDescription();

const inStock = computed<boolean>(
  () => !!(optionsStore.activeOption as { inStock?: boolean } | null)?.inStock
);
const isOptionSelected = computed<boolean>(() => optionsStore.isOptionSelected);

// First option with quantity=1 to bulk discount array
const defaultItem = computed<MaybeStoreBulkDiscount>(() => {
  return {
    quantity: 1,
    price_before: productStore.price.before || undefined,
    price_unit: productStore.price.initial_unit || undefined
  };
});

const bulkDiscounts = computed<MaybeStoreBulkDiscount[]>(() => {
  return [defaultItem.value, ...quantityStore.bulkDiscounts];
});

// Calculate discount on item
const getDiscountFormatted = (item: MaybeStoreBulkDiscount) => {
  const discount =
    item.price_before! * item.quantity! - item.price_unit! * item.quantity!;

  // We're not sure we'll get a number out because of... JS...
  if (isNaN(discount) || !discount) return '';

  return formattedPrice(discount);
};

const modalTitle = computed(() => {
  const selectedVariant =
    optionsStore.activeOption as MaybeStoreSelectedVariant | null;

  if (selectedVariant?.title) {
    return `${productName} - ${selectedVariant.title}`;
  }
  return productName;
});

const closestQuantityItem = computed<MaybeStoreBulkDiscount | undefined>(
  () => quantityStore.selectedBulkDiscount(qty.value) ?? defaultItem.value
);

const selectedBulkDiscountPct = computed<MaybeStoreBulkDiscount | undefined>(
  () => {
    const bulk =
      qty.value === 1
        ? defaultItem.value
        : quantityStore.selectedBulkDiscount(qty.value);

    return bulk?.discount_percentage;
  }
);

const highestDiscountItemPct = computed(() => {
  const bulk = quantityStore.highestDiscountItem;
  return bulk && bulk.discount_percentage;
});

const salesPrice = computed(() =>
  closestQuantityItem.value && closestQuantityItem.value.price_unit
    ? formattedPrice(closestQuantityItem.value.price_unit * qty.value)
    : ''
);

const regularPrice = computed(() =>
  closestQuantityItem.value && closestQuantityItem.value.price_before
    ? formattedPrice(closestQuantityItem.value.price_before * qty.value)
    : ''
);

const image = computed<string | null | undefined>(() => {
  return (
    (
      optionsStore.activeOption as {
        consolidatedImageUrl?: string;
      } | null
    )?.consolidatedImageUrl ||
    productStore.mainImageUrl ||
    window.Hobbii?.fallbackImageUrl
  );
});

const openModal = () => {
  quantityStore.showQuantityModal = true;
  qty.value = quantityStore.quantity;
};

const submitQuantityAndClose = () => {
  quantityStore.showQuantityModal = false;
  quantityStore.updateQuantity(qty.value);
};
</script>
